import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useReducer,
	useRef,
} from 'react';

import {ACTIONS} from './constants';

import _ from 'underscore';

import reducer, {initialState} from './reducer';

type StoreContextType = {
	state: typeof initialState;

	update(progress: number): void;
};

const StoreContext = createContext<StoreContextType>({} as any);

export const useProgressStore = () =>
	useContext<StoreContextType>(StoreContext);

export const ProgressStoreProvider = ({
	children,
}: React.PropsWithChildren<any>) => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const updateTimeoutRef = useRef<NodeJS.Timeout>();
	const updateRequestedAtRef = useRef<number>(0);

	const update = useCallback((progress: number) => {
		updateTimeoutRef.current && clearTimeout(updateTimeoutRef.current);

		const now = Date.now();
		if (!updateRequestedAtRef.current) updateRequestedAtRef.current = now;

		if (now - updateRequestedAtRef.current > 20 || progress === 100) {
			dispatch({type: ACTIONS.UPDATE, progress});
			updateTimeoutRef.current = setTimeout(() => {
				dispatch({type: ACTIONS.UPDATE, progress});
			}, 30);
		}

		if (progress === 100) {
			updateRequestedAtRef.current = 0;
		}

		return () => {
			updateTimeoutRef.current && clearTimeout(updateTimeoutRef.current);
		};
	}, []);

	const storeValue: StoreContextType = useMemo(
		() => ({
			state,
			update,
		}),
		[state, update],
	);

	return (
		<StoreContext.Provider value={storeValue}>{children}</StoreContext.Provider>
	);
};
